import React, { useState, useRef } from "react";
import { useFormCfg, useFormSub, SecTitle, ExtraFields } from "@/lib/formcfg";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, Crosshair, User, Briefcase, GraduationCap, Languages, Award, Wrench } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useLocation } from "@/context/LocationContext";
import { COUNTRIES, PHONE_CODES, CITIES_BY_COUNTRY, cityCoords, nearCountries } from "@/lib/places";
import { SUBCATEGORIES } from "@/lib/categories";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PROFESSIONS = SUBCATEGORIES.pune;

const JOB_TYPES = ["Full-time", "Part-time", "Vikarie", "Praktikë"];
const EXPERIENCE = ["Pa përvojë", "Me përvojë"];
const SKILLS = ["Ndërtim", "Hidraulikë", "Elektrikë", "Vozitje", "Magazinë", "Kuzhinë", "Pastrim", "IT", "Shitje", "Bujqësi", "Mekanikë", "Saldim", "Kujdes", "Administratë"];
const LANGS = ["Shqip", "Suedisht", "Anglisht", "Gjermanisht", "Norvegjisht", "Daneze", "Frëngjisht", "Italisht"];
const LICENSES = ["A", "B", "BE", "C", "C1", "CE", "D", "D1"];

function imgUrl(path) { return path; }
async function uploadImage(file) {
  const fd = new FormData(); fd.append("file", file);
  const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return imgUrl(r.data.url);
}
function parsePhone(p) { if (!p) return { code: "+46", num: "" }; const m = p.trim().match(/^(\+\d+)\s*(.*)$/); return m ? { code: m[1], num: m[2].replace(/\s/g, "") } : { code: "+46", num: p }; }

function Card({ icon: Icon, title: t0, children, desc }) {
  const fc = useFormCfg();
  if (fc.hidden(t0) && !fc.edit) return null;
  const title = <SecTitle t={t0} />;
  return (
    <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mt-3">
      <div className="flex items-center gap-2 mb-1">{Icon && <Icon className="w-4 h-4 text-emerald-500" />}<h2 className="font-bold text-slate-800 text-sm uppercase tracking-wide">{title}</h2></div>
      {desc && <p className="text-xs text-slate-400 mb-3">{desc}</p>}
      <div className={desc ? "" : "mt-3"}>{children}</div>
    </section>
  );
}
const label = "text-sm font-semibold text-slate-700 mb-1 block";
const fld = "w-full bg-slate-50 border border-slate-200 rounded-xl h-11 px-3 text-sm outline-none";

function Chips({ options, value, onToggle, sel = "bg-emerald-500 text-white border-transparent" }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = value.includes(o);
        return <button key={o} type="button" data-testid={`chip-${o}`} onClick={() => onToggle(o)} className={`text-sm font-semibold px-3 py-1.5 rounded-full border ${on ? sel : "bg-slate-50 text-slate-600 border-slate-200"}`}>{o}</button>;
      })}
    </div>
  );
}

export function PunekerkuesForm({ onDone, initial }) {
  const nav = useNavigate();
  const { requestGPS } = useLocation();
  const d0 = initial?.data || {};
  const [saving, setSaving] = useState(false);
  const photoRef = useRef(null);
  const [photo, setPhoto] = useState(initial?.images?.[0] || "");
  const [uploading, setUploading] = useState(false);
  const [f, setF] = useState({
    name: initial?.title || "", profession: initial?.subcategory || "", about: initial?.description || "",
    country: initial?.country || "", city: initial?.city || "",
    exp_level: d0.experience_level || "", exp_years: d0.experience_years || "", field: d0.field || "",
    prev: d0.prev_positions || "", education: d0.education || "", seeking: d0.seeking || "",
    instagram: initial?.instagram || "", email: initial?.email || "",
  });
  useFormSub(f.profession);
  const [coords, setCoords] = useState(initial?.lat != null ? { lat: initial.lat, lng: initial.lng } : null);
  const [phone, setPhone] = useState(parsePhone(initial?.phone));
  const [hasWhats, setHasWhats] = useState(initial ? !!initial.whatsapp : true);
  const [jobTypes, setJobTypes] = useState(d0.job_types || []);
  const [skills, setSkills] = useState(d0.skills || []);
  const [languages, setLanguages] = useState(d0.languages || []);
  const [licenses, setLicenses] = useState(d0.licenses || []);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const pickPhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const url = await uploadImage(file); setPhoto(url); toast.success("Fotoja u ngarkua"); }
    catch (er) { toast.error(errMsg(er)); } finally { setUploading(false); if (photoRef.current) photoRef.current.value = ""; }
  };
  const gps = async () => { try { const c = await requestGPS(); setCoords({ lat: c.lat, lng: c.lng }); toast.success("Vendndodhja u ruajt"); } catch { toast.error("Nuk u mor vendndodhja"); } };

  const submit = async () => {
    if (!f.name) return toast.error("Shkruaj emrin tënd");
    if (!f.profession) return toast.error("Zgjidh profesionin");
    if (!f.country || !f.city) return toast.error("Plotëso qytetin dhe shtetin");
    setSaving(true);
    const c = coords || cityCoords(f.country, f.city);
    const payload = {
      category: "punekerkues", subcategory: f.profession, title: f.name, description: f.about,
      price: null, price_unit: "", country: f.country, city: f.city, address: "",
      lat: c?.lat ?? null, lng: c?.lng ?? null, images: photo ? [photo] : [],
      phone: phone.num ? `${phone.code} ${phone.num}` : "",
      whatsapp: phone.num && hasWhats ? `${phone.code}${phone.num}` : "",
      instagram: f.instagram, email: f.email,
      data: { job_types: jobTypes, experience_level: f.exp_level, experience_years: f.exp_years, field: f.field, prev_positions: f.prev, skills, languages, education: f.education, licenses, seeking: f.seeking },
    };
    try {
      const r = initial ? await api.put(`/listings/${initial.id}`, payload) : await api.post("/listings", payload);
      toast.success(initial ? "Profili u ruajt" : "Profili u krijua!");
      const id = initial ? initial.id : r.data.id;
      onDone ? onDone(id) : nav(`/punekerkues/${id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="gs-fade px-4 pt-4 pb-10 bg-slate-50">
      <button data-testid="seeker-back" onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-slate-500 mb-2"><ArrowLeft className="w-4 h-4" />{initial ? "Anulo" : "Kthehu"}</button>
      <h1 className="text-2xl font-extrabold text-slate-900">{initial ? "Ndrysho profilin" : "Krijo profilin tim"}</h1>
      <p className="text-sm text-slate-400 mt-0.5">Punëdhënësit do të të gjejnë te Kërko Punë.</p>

      <Card icon={User} title="Foto & Informacioni">
        <div className="flex flex-col items-center mb-4">
          <input ref={photoRef} type="file" accept="image/*" onChange={pickPhoto} className="hidden" data-testid="seeker-photo-input" />
          <button type="button" onClick={() => photoRef.current?.click()} data-testid="seeker-photo-btn" className="relative w-24 h-24 rounded-full bg-emerald-50 ring-2 ring-emerald-200 overflow-hidden flex items-center justify-center">
            {photo ? <img src={photo} alt="profil" className="w-full h-full object-cover" /> : <User className="w-10 h-10 text-emerald-300" />}
            <span className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] py-0.5">{uploading ? "..." : "Foto"}</span>
          </button>
          <span className="text-xs text-slate-400 mt-1.5">Foto profili (opsionale)</span>
        </div>
        <label className={label}>Emri *</label>
        <Input data-testid="seeker-name" placeholder="Emri dhe mbiemri" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl h-11" />
        <label className={`${label} mt-3`}>Profesioni *</label>
        <select data-testid="seeker-profession" value={f.profession} onChange={(e) => setF({ ...f, profession: e.target.value })} className={fld}><option value="">Zgjidh...</option>{PROFESSIONS.map((s) => <option key={s}>{s}</option>)}</select>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div><label className={label}>Shteti *</label><select data-testid="seeker-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value, city: "" })} className={`${fld} px-2`}><option value="">Zgjidh</option>{nearCountries().map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}</select></div>
          <div><label className={label}>Qyteti *</label><select data-testid="seeker-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} disabled={!f.country} className={`${fld} px-2 disabled:text-slate-400`}><option value="">{f.country ? "Zgjidh" : "Zgjidh shtetin"}</option>{Object.keys(CITIES_BY_COUNTRY[f.country] || {}).map((c) => <option key={c}>{c}</option>)}</select></div>
        </div>
        <button data-testid="seeker-gps" onClick={gps} className="w-full mt-3 flex items-center justify-center gap-2 bg-emerald-500 text-white rounded-xl h-11 font-semibold"><Crosshair className="w-4 h-4" />Përdor vendndodhjen time</button>
        {coords && <p className="text-xs text-emerald-600 mt-2 text-center">✓ Koordinatat u ruajtën</p>}
      </Card>

      <Card title="Rreth meje">
        <Textarea data-testid="seeker-about" placeholder="Prezanto veten shkurt..." value={f.about} onChange={(e) => setF({ ...f, about: e.target.value })} className="rounded-xl" />
      </Card>

      <Card icon={Briefcase} title="Po kërkoj" desc="Çfarë pune po kërkon?">
        <Input data-testid="seeker-seeking" placeholder="p.sh. Po kërkoj punë si shofer" value={f.seeking} onChange={(e) => setF({ ...f, seeking: e.target.value })} className="rounded-xl h-11 mb-3" />
        <Chips options={JOB_TYPES} value={jobTypes} onToggle={(v) => toggle(jobTypes, setJobTypes, v)} />
      </Card>

      <Card icon={Award} title="Përvoja">
        <div className="flex gap-2 mb-3">{EXPERIENCE.map((e) => <button key={e} type="button" data-testid={`exp-${e}`} onClick={() => setF({ ...f, exp_level: f.exp_level === e ? "" : e })} className={`flex-1 text-sm font-semibold py-2.5 rounded-xl border ${f.exp_level === e ? "bg-emerald-500 text-white border-transparent" : "bg-slate-50 text-slate-600 border-slate-200"}`}>{e}</button>)}</div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className={label}>Vite përvojë</label><Input data-testid="seeker-years" placeholder="p.sh. 5" value={f.exp_years} onChange={(e) => setF({ ...f, exp_years: e.target.value })} className="rounded-xl h-11" /></div>
          <div><label className={label}>Fusha e punës</label><Input data-testid="seeker-field" placeholder="p.sh. Ndërtimtari" value={f.field} onChange={(e) => setF({ ...f, field: e.target.value })} className="rounded-xl h-11" /></div>
        </div>
        <label className={`${label} mt-3`}>Pozitat e mëparshme</label>
        <Textarea data-testid="seeker-prev" placeholder="Ku ke punuar më parë..." value={f.prev} onChange={(e) => setF({ ...f, prev: e.target.value })} className="rounded-xl" />
      </Card>

      <Card icon={Wrench} title="Aftësitë">
        <Chips options={SKILLS} value={skills} onToggle={(v) => toggle(skills, setSkills, v)} />
      </Card>

      <Card icon={Languages} title="Gjuhët">
        <Chips options={LANGS} value={languages} onToggle={(v) => toggle(languages, setLanguages, v)} sel="bg-blue-500 text-white border-transparent" />
      </Card>

      <Card icon={GraduationCap} title="Arsimi dhe kualifikimet">
        <Textarea data-testid="seeker-education" placeholder="Shkolla, kurse, certifikata, licenca..." value={f.education} onChange={(e) => setF({ ...f, education: e.target.value })} className="rounded-xl" />
      </Card>

      <Card title="Patenta e shoferit">
        <Chips options={LICENSES} value={licenses} onToggle={(v) => toggle(licenses, setLicenses, v)} sel="bg-slate-800 text-white border-transparent" />
      </Card>

      <Card title="Kontakt">
        <label className={label}>Telefon</label>
        <div className="flex gap-2">
          <select data-testid="seeker-phone-code" value={phone.code} onChange={(e) => setPhone({ ...phone, code: e.target.value })} className="bg-slate-50 border border-slate-200 rounded-xl h-11 px-2 text-sm font-semibold outline-none">{PHONE_CODES.map((c) => <option key={c.dial} value={c.dial}>{c.flag} {c.dial}</option>)}</select>
          <Input data-testid="seeker-phone" inputMode="tel" placeholder="Numri i telefonit" value={phone.num} onChange={(e) => setPhone({ ...phone, num: e.target.value })} className="rounded-xl h-11 flex-1" />
        </div>
        <button type="button" data-testid="toggle-whatsapp" onClick={() => setHasWhats(!hasWhats)} className={`w-full mt-2 flex items-center justify-center gap-2 text-sm font-semibold px-3 py-2.5 rounded-xl border ${hasWhats ? "border-green-400 bg-green-50 text-green-700" : "border-slate-200 text-slate-500"}`}><span className={`w-4 h-4 rounded border flex items-center justify-center ${hasWhats ? "bg-green-500 border-green-500" : "border-slate-300"}`}>{hasWhats && <span className="text-white text-[10px]">✓</span>}</span>Ky numër ka WhatsApp</button>
        <div className="mt-3"><label className={label}>Email</label><Input data-testid="seeker-email" placeholder="email@shembull.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-xl h-11" /></div>
        <div className="mt-3"><label className={label}>Instagram</label><Input data-testid="seeker-instagram" placeholder="username ose link" value={f.instagram} onChange={(e) => setF({ ...f, instagram: e.target.value })} className="rounded-xl h-11" /></div>
      </Card>

      <ExtraFields />
      <Button data-testid="seeker-submit" onClick={submit} disabled={saving} className="w-full py-3.5 rounded-2xl bg-emerald-500 hover:bg-emerald-600 font-bold text-base mt-4 shadow-lg shadow-emerald-500/30">{saving ? "Duke ruajtur..." : initial ? "Ruaj ndryshimet" : "Krijo profilin"}</Button>
    </div>
  );
}

export default PunekerkuesForm;
